import { customThemeProps } from "@/theme";
import {
  isBidLoanException,
  isValidationError,
  ValidationError,
} from "@/types/bidApi";
import { AuthResponse } from "@/types/resellersNUsers";
import { ThemeColors } from "@/types/theme";
import { createTheme } from "@mui/material/styles";
import { AxiosError } from "axios";
import { clsx, type ClassValue } from "clsx";
import { composeRenderProps } from "react-aria-components";
import { FieldValues, Path, UseFormSetError } from "react-hook-form";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

interface HandleAxiosErrorOnFormParams<T extends FieldValues> {
  error: AxiosError;
  setError: UseFormSetError<T>;
  setAlertMessage: (message: string) => void;
}

export function handleAxiosErrorOnForm<T extends FieldValues>({
  error,
  setError,
  setAlertMessage,
}: HandleAxiosErrorOnFormParams<T>) {
  const responseData = error.response?.data;

  if (Array.isArray(responseData)) {
    setAlertMessage(responseData[0]);
    return;
  }

  if (isBidLoanException(responseData)) {
    setAlertMessage(responseData.exception_message);
    return;
  }

  if (isValidationError(responseData)) {
    handleValidationErrors(responseData, setError, setAlertMessage);
    return;
  }

  setAlertMessage("Algo deu errado. Por favor, tente novamente.");
}

function handleValidationErrors<T extends FieldValues>(
  validationError: ValidationError,
  setError: UseFormSetError<T>,
  setAlertMessage: (message: string) => void
) {
  Object.entries(validationError).forEach(([field, errors]) => {
    if (Array.isArray(errors)) {
      setFieldErrors(field, errors, setError);
    } else {
      setNestedFieldErrors(field, errors, setError);
    }
  });

  if (validationError.non_field_errors) {
    setAlertMessage((validationError.non_field_errors as string[])[0]);
  }
}

function setFieldErrors<T extends FieldValues>(
  field: string,
  errors: string[],
  setError: UseFormSetError<T>
) {
  errors.forEach((error: string) => {
    setError(field as Path<T>, {
      type: "manual",
      message: error,
    });
  });
}

function setNestedFieldErrors<T extends FieldValues>(
  field: string,
  errors: Record<string, string[]>,
  setError: UseFormSetError<T>
) {
  Object.entries(errors).forEach(([_, nestedErrors]) => {
    nestedErrors.forEach((error: string) => {
      setError(field as Path<T>, {
        type: "manual",
        message: error,
      });
    });
  });
}

export function getKeyByValue<T extends Record<string, string>>(
  obj: T,
  value: string
): keyof T | undefined {
  return (Object.keys(obj) as Array<keyof T>).find((key) => obj[key] === value);
}

interface CheckForPermissionParams {
  authenticatedUser: AuthResponse;
  permissionToCheck: string;
}

export function checkForPermission({
  authenticatedUser,
  permissionToCheck,
}: CheckForPermissionParams) {
  return authenticatedUser.usuario?.permissions.includes(permissionToCheck);
}

export function isPoolingServiceDisabled(
  poolingService: "notifications" | "pending-balance-requests"
): boolean {
  const env = import.meta.env;
  return (
    !env.PROD &&
    env.VITE_DISABLED_POOLING_SERVICES &&
    env.VITE_DISABLED_POOLING_SERVICES.includes(poolingService)
  );
}

export function isMockingEnabledForService(service: "dashboard") {
  const env = import.meta.env;
  const enabled =
    !env.PROD &&
    env.VITE_MOCKED_SERVICES &&
    env.VITE_MOCKED_SERVICES.includes(service);
  if (enabled) {
    console.log("Mocking service", service);
  }
  return enabled;
}

export function timeout(ms: number) {
  return new Promise((res) => setTimeout(res, ms));
}

export function numberAsMagnitude(n: number, m: "K" | "MM"): string {
  const magnitude = m === "K" ? 1000 : 1_000_000;
  return `${numberToPtBRLocale(n / magnitude, { decimalPlaces: 0 })}${m}`;
}

export function numberToPtBRLocale(
  n: number,
  opts?: { decimalPlaces: number | undefined } | undefined
) {
  return n.toLocaleString("pt-BR", {
    minimumFractionDigits: opts?.decimalPlaces,
    maximumFractionDigits: opts?.decimalPlaces,
  });
}

export function composeTailwindRenderProps<T>(
  className: string | ((v: T) => string) | undefined,
  tw: string
): string | ((v: T) => string) {
  return composeRenderProps(className, (className) => twMerge(tw, className));
}

export function extendCurrentThemeColors(colors: ThemeColors) {
  return createTheme({
    ...customThemeProps,
    palette: {
      ...customThemeProps.palette,
      primary: {
        main: colors.primary,
        contrastText: colors.contrastText,
      },
      error: {
        main: colors.error,
        contrastText: colors.contrastText,
      },
      warning: {
        main: colors.warning,
        contrastText: colors.contrastText,
      },
      success: {
        main: colors.success,
        contrastText: colors.contrastText,
      },
      text: {
        primary: colors.text,
      },
    },
  });
}

export function safeNotificationCheck(
  check: (nt: typeof Notification) => boolean
): boolean {
  // Repetitve check that must be done before referencing the Notification class
  return "Notification" in window && check(Notification);
}

export function assertNever(x: never): never {
  throw new Error(`Expected never, got: ${x}`);
}

interface RGB {
  r: number;
  g: number;
  b: number;
}

interface HSL {
  h: number;
  s: number;
  l: number;
}

function hexToRgb(hex: string): RGB {
  const cleanHex = hex.replace('#', '');
  return {
    r: parseInt(cleanHex.substring(0, 2), 16),
    g: parseInt(cleanHex.substring(2, 4), 16),
    b: parseInt(cleanHex.substring(4, 6), 16),
  };
}

function rgbToHsl({ r, g, b }: RGB): HSL {
  const rNorm = r / 255;
  const gNorm = g / 255;
  const bNorm = b / 255;
  
  const max = Math.max(rNorm, gNorm, bNorm);
  const min = Math.min(rNorm, gNorm, bNorm);
  const delta = max - min;
  
  const l = (max + min) / 2;
  
  if (delta === 0) {
    return { h: 0, s: 0, l };
  }
  
  const s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min);
  
  let h = 0;
  switch (max) {
    case rNorm:
      h = ((gNorm - bNorm) / delta + (gNorm < bNorm ? 6 : 0)) / 6;
      break;
    case gNorm:
      h = ((bNorm - rNorm) / delta + 2) / 6;
      break;
    case bNorm:
      h = ((rNorm - gNorm) / delta + 4) / 6;
      break;
  }
  
  return { h, s, l };
}

function hslToRgb({ h, s, l }: HSL): RGB {
  if (s === 0) {
    const gray = Math.round(l * 255);
    return { r: gray, g: gray, b: gray };
  }
  
  const hueToRgb = (p: number, q: number, t: number): number => {
    let normalizedT = t;
    if (normalizedT < 0) normalizedT += 1;
    if (normalizedT > 1) normalizedT -= 1;
    if (normalizedT < 1/6) return p + (q - p) * 6 * normalizedT;
    if (normalizedT < 1/2) return q;
    if (normalizedT < 2/3) return p + (q - p) * (2/3 - normalizedT) * 6;
    return p;
  };
  
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  
  return {
    r: Math.round(hueToRgb(p, q, h + 1/3) * 255),
    g: Math.round(hueToRgb(p, q, h) * 255),
    b: Math.round(hueToRgb(p, q, h - 1/3) * 255),
  };
}

function rgbToHex({ r, g, b }: RGB): string {
  const toHex = (n: number): string => {
    const hex = n.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Returns the first triadic color of a given hex color.
 * Uses HSL color space to rotate the hue by 120 degrees.
 * Triadic colors are evenly spaced around the color wheel (120° apart).
 * 
 * @param hexColor - The hex color string (with or without #)
 * @returns The first triadic color as a hex string
 * 
 * @see https://en.wikipedia.org/wiki/Harmony_(color)
 * @see https://www.w3.org/TR/css-color-3/#hsl-color
 */
export function getTriadicColor(hexColor: string): string {
  const rgb = hexToRgb(hexColor);
  const hsl = rgbToHsl(rgb);
  
  const triadicHsl: HSL = {
    ...hsl,
    h: (hsl.h + 1/3) % 1,
  };
  
  const triadicRgb = hslToRgb(triadicHsl);
  return rgbToHex(triadicRgb);
}
